import "./index.scss";
import { FC, useState } from "react";
import { Button, Input, Modal, Table, message } from "antd";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "../../api/request";
import { EServerItemStatus, UserServerItem } from "../../constant";
import { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import copy from "copy-to-clipboard";

interface TableParams {
  pagination?: TablePaginationConfig;
}

const UserServerManage: FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isShowModal, setIsShowModal] = useState(false);
  const [editRemark, setEditRemark] = useState("");
  const [edittingId, setEdittingId] = useState<number>(0);

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 20,
    },
  });

  const { data, isFetching, isError } = useQuery({
    queryKey: ["user-server-list", { page: tableParams.pagination?.current, page_size: tableParams.pagination?.pageSize }],
    queryFn: async ({ queryKey }) => {
      const [_key, data] = queryKey;
      return request("user/server-manager/", data);
    },

    placeholderData: keepPreviousData,
  });

  const { data: isAdminData } = useQuery({
    queryKey: ["user-is-admin"],
    queryFn: async () => {
      return request("user/is-admin/");
    },
  });

  const updateRemarkMutation = useMutation({
    mutationFn: async (data: { res_id: number; remark: string }) => {
      return request("user/update-server-remark/", data);
    },
    onSuccess: (data) => {
      //
    },
    onSettled: (data, error, variables, context) => {
      setIsShowModal(false);
      setEditRemark("");
      setEdittingId(0);
      queryClient.invalidateQueries({ queryKey: ["user-server-list"] });
    },
  });

  /* #region Renew */
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<UserServerItem[]>([]);
  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: UserServerItem[]) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys, selectedRows);
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(selectedRows);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  const renewMutation = useMutation({
    mutationFn: async (data: { selected_products: string[] }) => {
      return api.post(
        "/wp-admin/admin-ajax.php",
        {
          action: "add_to_cart_renew",
          ...data,
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
    },
    onSuccess: async (data) => {
      console.log(data);

      window.location.pathname = "/checkout";
    },
  });
  const handleRenew = () => {
    console.log("当前操作商品信息:", selectedRows);
    // getCatrItemsMutation.mutate();
    renewMutation.mutate({
      selected_products: selectedRows.map((item) => `${item.product_id}|${item.res_id}`),
    });
  };
  /* #endregion */

  const columns: ColumnsType<UserServerItem> = [
    {
      title: "Name",
      dataIndex: "res_name",
      key: "res_name",
    },
    {
      title: "IP",
      dataIndex: "res_ip",
      key: "res_ip",
    },
    {
      title: "Port",
      dataIndex: "res_port",
      key: "res_port",
    },
    {
      title: "Username",
      dataIndex: "res_user",
      key: "res_user",
    },
    {
      title: "Password",
      dataIndex: "res_password",
      key: "res_password",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: number) => {
        return {
          [EServerItemStatus.PENDING]: "Pending",
          [EServerItemStatus.ACTIVE]: "Active",
          [EServerItemStatus.EXPIRED]: "Expired",
        }[status];
      },
    },
    {
      title: "Expire Date",
      dataIndex: "res_renew_time",
      key: "res_renew_time",
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      render(value: string, record) {
        return (
          <span>
            {value}
            <EditOutlined
              onClick={() => {
                setEdittingId(record.res_id);
                setEditRemark(value);
                setIsShowModal(true);
              }}
            />
          </span>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "res_id",
      key: "res_id",
      render(value: string, record) {
        return (
          <div className="tableOperation">
            <Button
              type="primary"
              onClick={() => {
                copy(`${record.res_ip}:${record.res_port}:${record.res_user}:${record.res_password}`);
                message.success("Copied!");
              }}
              disabled={record.status !== EServerItemStatus.ACTIVE}
            >
              Copy
            </Button>
            <Button
              type="primary"
              onClick={() => {
                renewMutation.mutate({
                  selected_products: [`${record.product_id}|${record.res_id}`],
                });
              }}
              loading={renewMutation.isPending}
              disabled={record.status !== EServerItemStatus.ACTIVE}
            >
              Renew
            </Button>
          </div>
        );
      },
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        ...pagination,
      },
    });
  };

  if (isError) {
    return <div>API Error</div>;
  }

  return (
    <main className="main">
      {/* <div>My Server</div> */}

      <div className="operations">
        <span>
          <Button id="renew-button" onClick={handleRenew} loading={renewMutation.isPending} disabled={!hasSelected} type="primary">
            Batch Renew
          </Button>
        </span>
        {isAdminData?.data.data && (
          <span className="adminButtons">
            <Button
              type="primary"
              onClick={() => {
                navigate("/manage-user-server");
              }}
            >
              服务器管理
            </Button>
            <Button
              type="primary"
              onClick={() => {
                navigate("/manage-server");
              }}
            >
              自有服务器管理
            </Button>
          </span>
        )}
      </div>
      <Table
        dataSource={data ? (data?.data?.data as UserServerItem[]) : []}
        columns={columns}
        loading={isFetching}
        rowKey={"res_id"}
        pagination={{
          pageSize: data?.data.page_size,
          current: data?.data.page,
          total: data?.data.count,
          showTotal: (total) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        rowSelection={rowSelection}
      />
      <Modal
        title={"Update Remark"}
        open={isShowModal}
        onCancel={() => {
          setIsShowModal(false);
        }}
        onOk={() => {
          updateRemarkMutation.mutate({
            res_id: edittingId,
            remark: editRemark,
          });
        }}
        okButtonProps={{
          loading: updateRemarkMutation.isPending,
        }}
      >
        <Input
          value={editRemark}
          onChange={(e) => {
            setEditRemark(e.target.value);
          }}
        />
      </Modal>
    </main>
  );
};

export default UserServerManage;
